import type { ScreenerData } from "epsm-api-types/models";
import { useEffect, useState } from "react";
import { formatValueNumber } from "@/components/widgets/helpers/FormattingHelpers";
import {
	coloredPercentTD,
	coloredTextConsensusTD,
	getDateString,
} from "@/components/widgets/Revision/lib/helpers";
import { UseData as UseDataRevision } from "@/components/widgets/Revision/lib/services/UseData";
import type { RevisionExt } from "@/components/widgets/Revision/types";
import { sortedByDateAndType } from "@/components/widgets/Revision/utils/RevisionTableUtils";
import { UseData as UseDataScreener } from "@/components/widgets/Screener/services/UseData";
import { UseData as UseDataTicker } from "@/components/widgets/Ticker/UseData";
import { cn } from "@/lib/utils";
import { KEY } from "@/shared/constants/keyboard";
import {
	COMPONENT_CONTAINER_CLASSES,
	MIN_MAX_COLUMN_CLASSES,
	TABLE_WIDTH_SIZES,
} from "../../../config";
import { TableWatermark } from "../TableWatermark";
import { revisionParams, screenerParams } from "./config";
import { useHandleTickerClick } from "./lib/hooks";

type CompetitorsGridProps = {
	tileId: string;
	ticker: string;
	lastRevisionDate: Date;
};

export const CompetitorsGrid = ({
	tileId,
	ticker,
	lastRevisionDate,
}: CompetitorsGridProps) => {
	const { execute: executeTicker } = UseDataTicker.create().useGetData();
	const { execute: executeRevision } = UseDataRevision.create().useGetData();
	const { execute: executeScreener } = UseDataScreener.create().useGetData();

	const [tickers, setTickers] = useState<string[] | undefined>(undefined);
	const [dataRevision, setDataRevision] = useState<RevisionExt[] | undefined>(
		undefined,
	);
	const [dataScreener, setDataScreener] = useState<ScreenerData[] | undefined>(
		undefined,
	);

	useEffect(() => {
		const getData = async () => {
			const data = await executeTicker({
				text: ticker,
				offset: 0,
				limit: 1,
			});

			if (!data?.data?.length) {
				setTickers([ticker]);
				return;
			}

			setTickers([ticker, ...(data.data[0].top_competitors ?? [])]);
		};
		getData();
	}, [ticker, executeTicker]);

	useEffect(() => {
		if (!tickers?.length) return;

		const getData = async () => {
			const result = await executeRevision({
				params: {
					...revisionParams,
					filter: [
						{
							name: "ticker",
							condition: "in",
							value: tickers,
						},
						{
							name: "date",
							condition: "eq",
							value: getDateString(lastRevisionDate),
						},
					],
					sort: [{ name: "ticker", order: 1 }],
				},
			});
			setDataRevision(result?.data?.length ? result?.data : undefined);
		};
		getData();
	}, [tickers, lastRevisionDate, executeRevision]);

	useEffect(() => {
		const getData = async () => {
			if (!tickers?.length) return;
			const result = await executeScreener({
				params: {
					...screenerParams,
					filter: [
						{
							name: "ticker",
							condition: "in",
							value: tickers,
						},
					],
				},
			});
			setDataScreener(result?.data);
		};
		getData();
	}, [tickers, executeScreener]);

	const handleTickerClick = useHandleTickerClick(tileId);

	const filteredTickers = tickers?.filter(
		(item) =>
			dataRevision?.find((revisionItem) => revisionItem.ticker === item)?.dp?.ns
				?.ee?.length,
	);

	if (!filteredTickers?.length) return false;

	return (
		<div className={COMPONENT_CONTAINER_CLASSES}>
			<h3 className="text-light-gray text-lg">
				Peers' RS and Price Chg From Last Earnings Date
			</h3>
			<div className={cn("overflow-auto", TABLE_WIDTH_SIZES)}>
				<table
					className="table-fixed w-full border-collapse text-right text-light-gray border-gray border-2"
					id="competitors-table"
				>
					<thead className="border-gray border-2">
						<tr className="border-gray border-2">
							<th
								className={`border-gray border-2 text-left sticky left-0 z-10 ${MIN_MAX_COLUMN_CLASSES}`}
							>
								RS Ch%
							</th>
							<th
								className={`font-normal text-center ${MIN_MAX_COLUMN_CLASSES}`}
							>
								EPS
							</th>
							<th
								className={`font-normal text-center ${MIN_MAX_COLUMN_CLASSES}`}
							>
								Sales
							</th>
							<th
								className={`font-normal text-center ${MIN_MAX_COLUMN_CLASSES}`}
							>
								Price
							</th>
							<th
								className={`font-normal text-center ${MIN_MAX_COLUMN_CLASSES} border-gray border-2 `}
							>
								Consens.
							</th>
							<th
								className={`font-normal text-center ${MIN_MAX_COLUMN_CLASSES} border-gray border-2 `}
							>
								Earn. (D)
							</th>
						</tr>
					</thead>
					<tbody className="border-gray border-2">
						{filteredTickers?.map((value: string) => (
							<tr key={value} className="border-gray border-x-2">
								<td
									className="border-gray border-x-2 text-left"
									data-row-id={value}
									onClick={handleTickerClick}
									onKeyDown={(event) => {
										if (event.key === KEY.ENTER || event.key === KEY.SPACE) {
											handleTickerClick(event);
										}
									}}
								>
									{value}
								</td>
								{coloredPercentTD(
									dataRevision?.find((item) => item.ticker === value)?.rs_chg
										?.ns?.pct_chg_ed,
									"border-r-0 border-d-0",
								)}
								{coloredPercentTD(
									dataRevision?.find((item) => item.ticker === value)?.rss_chg
										?.ns?.pct_chg_ed,
									"border-r-0",
								)}
								{coloredPercentTD(
									(
										dataScreener?.find((item) => item.ticker === value)
											?.adj_close_pct_chg as { ETD: number }
									)?.ETD,
									"border-r-0",
								)}
								{coloredTextConsensusTD(
									sortedByDateAndType(
										dataRevision?.find((item) => item.ticker === value)?.dp?.ns
											?.ee,
									)?.[0]?.dispersion,
									"border-d-0 border-x-2 border-gray truncate",
								)}
								<td>
									{formatValueNumber(
										dataScreener?.find((item) => item.ticker === value)
											?.days_to_earn,
										"-",
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<TableWatermark />
		</div>
	);
};
